import { HttpErrorResponse } from '@angular/common/http';
import {ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {AssociationListResponse} from '@services/back-office/model/associationListResponse';
import {EnumToObjectModel} from '@shared/models/enum-to-object';
import {AssociationsService} from '@shared/services/associations.service';
import {SharedService} from '@shared/shared.service';
import {ToastrService} from 'ngx-toastr';
import {BehaviorSubject, catchError, EMPTY} from 'rxjs';
import {map, take} from 'rxjs/operators';
import {OrganizationSectionModel} from '@services/back-office/model/organizationSectionModel';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';

@Component({
    selector: 'app-garage-row-dialog',
    templateUrl: './garage-row-dialog.component.html',
    styleUrls: ['./garage-row-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class GarageRowDialogComponent implements OnInit, OnDestroy {
    /** Loading */
    loading$ = new BehaviorSubject(false);
    /** Form */
    garageRowForm: UntypedFormGroup;
    /** Garage Row Types */
    garageRowTypes: EnumToObjectModel[];

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            association: AssociationListResponse,
            garageRow?: OrganizationSectionModel,
        },
        public dialogRef: MatDialogRef<GarageRowDialogComponent>,
        private formBuilder: UntypedFormBuilder,
        private associationsService: AssociationsService,
        private translateService: TranslateService,
        private toastrService: ToastrService,
    ) {
    }

    ngOnInit(): void {
        if (!this.garageRowForm) {
            this.initForm();
        }
        /** Convert Enum To Object */
        this.garageRowTypes = SharedService.convertEnumToObject(OrganizationSectionModel.TypeEnum);
        /** Patch Form On Edit */
        if (this.data.garageRow) {
            this.patchForm();
        }
    }

    ngOnDestroy() {
        this.loading$.complete();
    }

    initForm() {
        this.garageRowForm = this.formBuilder.group({
            type: ['', [Validators.required]],
            number: ['', [Validators.required]],
        });
    }

    /** Patch Form On Edit */
    patchForm() {
        if (!this.garageRowForm) {
            this.initForm();
        }

        this.garageRowForm.patchValue({
            type: this.data.garageRow?.type,
            number: this.data.garageRow?.number,
        });
    }

    submitForm() {
        this.loading$.next(true);
        const formData = this.garageRowForm.getRawValue();
        const request: OrganizationSectionModel = {
            ...formData.type && {type: formData.type},
            ...formData.number && {number: formData.number},
        };

        if (!this.data.garageRow) {
            this.associationsService.addSection(this.data.association.id!, request).pipe(
                take(1),
                map((response: OrganizationSectionModel) => {
                    this.loading$.next(false);
                    this.closeDialog(true, response);
                }),
                catchError((error: HttpErrorResponse) => {
                    SharedService.showErrorWithTitle(
                        this.translateService,
                        this.toastrService,
                        `responseErrors.${error.error.message}`
                    );
                    this.loading$.next(false);
                    return EMPTY;
                })
            ).subscribe();
        } else {
            this.associationsService.editSection(this.data.garageRow.id!, request).pipe(
                take(1),
                map((response: OrganizationSectionModel) => {
                    this.loading$.next(false);
                    this.closeDialog(true, response);
                }),
                catchError((error: HttpErrorResponse) => {
                    SharedService.showErrorWithTitle(
                        this.translateService,
                        this.toastrService,
                        `responseErrors.${error.error.message}`
                    );
                    this.loading$.next(false);
                    return EMPTY;
                })
            ).subscribe();
        }
    }

    /** Close dialog */
    closeDialog(result: boolean, data?: any) {
        this.dialogRef.close({result, data});
    }
}
